/**
 * Mega Category Navigation Component
 * Left sidebar + right hero banner, subcategory panel overlays on hover
 */
import { Component } from '@theme/component';

class MegaCategoryNav extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.sidebar = this.querySelector('.mcn-sidebar');
    this.panel = this.querySelector('.mcn-panel');
    this.right = this.querySelector('.mcn-right');
    this.items = this.querySelectorAll('.mcn-sidebar__item');
    this.panelSections = this.querySelectorAll('.mcn-panel__section');

    // Tab navigation
    this.tabs = this.querySelectorAll('.mcn-tabs__tab');
    this.tabContents = this.querySelectorAll('.mcn-tabs__content');

    // Carousel elements
    this.carousel = this.querySelector('[data-carousel]');
    this.carouselTrack = this.querySelector('[data-carousel-track]');
    this.slides = this.querySelectorAll('.mcn-carousel__slide');
    this.prevBtn = this.querySelector('[data-carousel-prev]');
    this.nextBtn = this.querySelector('[data-carousel-next]');
    this.dots = this.querySelectorAll('[data-carousel-dot]');

    this.activeIndex = 0;
    this.hoverTimeout = null;
    this.hideTimeout = null;

    // Carousel state
    this.currentSlide = 0;
    this.totalSlides = this.slides.length;
    this.autoSlideTimer = null;

    this.bindEvents();
    this.initCarousel();
  }

  bindEvents() {
    // Only wire up hover behavior on devices that truly support hover (desktop).
    // On touch devices the synthesized `mouseenter` fires on the first tap and
    // changes the panel content, which makes the browser (esp. iOS Safari)
    // swallow that first `click` — forcing users to tap twice before the scroll
    // (bound to `click`) runs. Skipping hover on touch lets a single tap work.
    const canHover = window.matchMedia('(hover: hover)').matches;

    // Sidebar item hover → show panel with subcategories
    this.items.forEach((item, index) => {
      if (canHover) {
        item.addEventListener('mouseenter', () => {
          clearTimeout(this.hoverTimeout);
          clearTimeout(this.hideTimeout);
          this.hoverTimeout = setTimeout(() => {
            this.showCategory(index);
            this.showPanel();
          }, 80);
        });
      }

      item.addEventListener('click', (e) => {
        // Only prevent default to show panel; don't block navigation
        e.preventDefault();
        this.showCategory(index);
        this.showPanel();
        // On mobile there is no hover, and the subcategory panel sits below the
        // full category list — so a tap looks like "nothing happened". Bring the
        // subcategory area into view so the user clearly sees it opened.
        this.scrollToPanelOnMobile();
      });
    });

    // Panel card clicks → ensure navigation works
    if (this.panel) {
      this.panel.addEventListener('click', (e) => {
        const card = e.target.closest('.mcn-panel__card, .mcn-panel__group-link');
        if (card && card.href) {
          e.stopPropagation();
          window.location.href = card.href;
        }
      });

      // Keep panel visible when hovering over it (desktop only)
      if (canHover) {
        this.panel.addEventListener('mouseenter', () => {
          clearTimeout(this.hideTimeout);
        });
        this.panel.addEventListener('mouseleave', () => {
          this.scheduleHidePanel();
        });
      }
    }

    // Sidebar leave → start hide timer (desktop only)
    if (this.sidebar && canHover) {
      this.sidebar.addEventListener('mouseleave', () => {
        this.scheduleHidePanel();
      });
    }

    // Tab click events
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        this.showTab(index);
      });
    });
  }

  showCategory(index) {
    this.activeIndex = index;

    // Update sidebar active state
    this.items.forEach((item, i) => {
      item.classList.toggle('mcn-sidebar__item--active', i === index);
    });

    // Update panel sections
    this.panelSections.forEach((section, i) => {
      section.classList.toggle('mcn-panel__section--active', i === index);
    });

    // On mobile, replay a short fade/slide-in on the newly activated section so
    // the content refresh is visually obvious (there is no overlay animation there).
    if (this.isMobile()) {
      const activeSection = this.panelSections[index];
      if (activeSection) {
        activeSection.classList.remove('mcn-panel__section--enter');
        // Force reflow so the animation restarts even on repeated taps.
        void activeSection.offsetWidth;
        activeSection.classList.add('mcn-panel__section--enter');
      }
    }
  }

  isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  scrollToPanelOnMobile() {
    if (!this.isMobile() || !this.panel) return;
    // Defer to the next frame so the panel section has switched to display:flex
    // before we measure its position.
    requestAnimationFrame(() => {
      // Account for any sticky/fixed header so the panel isn't hidden behind it.
      const headerEl = document.querySelector(
        '.header-wrapper, .section-header, header[role="banner"], #shopify-section-header'
      );
      const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 0;
      const rect = this.panel.getBoundingClientRect();
      const targetTop = window.pageYOffset + rect.top - headerHeight - 8;
      window.scrollTo({ top: Math.max(targetTop, 0), behavior: 'smooth' });
    });
  }

  showPanel() {
    if (this.panel) {
      this.panel.classList.add('mcn-panel--visible');
    }
  }

  hidePanel() {
    if (this.panel) {
      this.panel.classList.remove('mcn-panel--visible');
    }
  }

  scheduleHidePanel() {
    clearTimeout(this.hideTimeout);
    this.hideTimeout = setTimeout(() => {
      this.hidePanel();
    }, 250);
  }

  showTab(index) {
    this.tabs.forEach((tab, i) => {
      tab.classList.toggle('mcn-tabs__tab--active', i === index);
    });

    this.tabContents.forEach((content, i) => {
      content.classList.toggle('mcn-tabs__content--active', i === index);
    });
  }

  /* === Carousel === */
  initCarousel() {
    if (this.totalSlides <= 1) return;

    // Arrow buttons
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToSlide(this.currentSlide - 1);
        this.resetAutoSlide();
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToSlide(this.currentSlide + 1);
        this.resetAutoSlide();
      });
    }

    // Dot buttons
    this.dots.forEach((dot) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        const idx = parseInt(dot.getAttribute('data-carousel-dot'), 10);
        this.goToSlide(idx);
        this.resetAutoSlide();
      });
    });

    // Touch / swipe support
    this.touchStartX = 0;
    this.touchEndX = 0;
    if (this.carousel) {
      this.carousel.addEventListener('touchstart', (e) => {
        this.touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });
      this.carousel.addEventListener('touchend', (e) => {
        this.touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe();
      }, { passive: true });

      // Pause auto-slide on hover
      this.carousel.addEventListener('mouseenter', () => {
        this.stopAutoSlide();
      });
      this.carousel.addEventListener('mouseleave', () => {
        this.startAutoSlide();
      });
    }

    // Start auto-slide
    this.startAutoSlide();
  }

  goToSlide(index) {
    if (index < 0) index = this.totalSlides - 1;
    if (index >= this.totalSlides) index = 0;
    this.currentSlide = index;

    if (this.carouselTrack) {
      this.carouselTrack.style.transform = `translateX(-${index * 100}%)`;
    }

    // Update dots
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('mcn-carousel__dot--active', i === index);
    });
  }

  handleSwipe() {
    const diff = this.touchStartX - this.touchEndX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        this.goToSlide(this.currentSlide + 1);
      } else {
        this.goToSlide(this.currentSlide - 1);
      }
      this.resetAutoSlide();
    }
  }

  startAutoSlide() {
    // Read auto_slide setting from data attribute or default to true
    const autoSlide = this.getAttribute('data-auto-slide') !== 'false';
    if (!autoSlide || this.totalSlides <= 1) return;

    const interval = parseInt(this.getAttribute('data-slide-interval'), 10) || 5;
    this.autoSlideTimer = setInterval(() => {
      this.goToSlide(this.currentSlide + 1);
    }, interval * 1000);
  }

  stopAutoSlide() {
    if (this.autoSlideTimer) {
      clearInterval(this.autoSlideTimer);
      this.autoSlideTimer = null;
    }
  }

  resetAutoSlide() {
    this.stopAutoSlide();
    this.startAutoSlide();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.hoverTimeout);
    clearTimeout(this.hideTimeout);
    this.stopAutoSlide();
  }
}

customElements.define('mega-category-nav', MegaCategoryNav);

export { MegaCategoryNav };
